import apiV2 from '../api/v2';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiError';

export interface IteroLinkedPractice {
    practiceId: string;
    name: string;
}

export interface IteroLiveCheck {
    ok: boolean;
    companyName?: string;
    doctors?: number;
    labs?: number;
    reason?: string;
}

export interface IteroStatus {
    connected: boolean;
    clinicId: number | null;
    accessTokenExpiresAt: string | null;
    sharedWith: IteroLinkedPractice[];
    live?: IteroLiveCheck;
}

export interface IteroClinic {
    clinicId: number;
    name: string;
    address: string;
    isPaired: boolean;
    linkedPractices: IteroLinkedPractice[];
    isCurrent: boolean;
}

export interface IteroRefreshResult {
    refreshed: boolean;
    reason?: 'not_due';
    repaired?: boolean;
    status: IteroStatus;
}

export interface IteroDisconnectResult {
    disconnected: boolean;
    unpairedAtItero: boolean;
    status: IteroStatus;
}

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
}

// These calls make the server log in to iTero with a headless browser (~10-15s).
const ITERO_SLOW_CALL_TIMEOUT_MS = 120_000;

export const iteroPath = (practiceId: string) =>
    `/practices/${encodeURIComponent(practiceId)}/itero`;

export async function fetchIteroStatus(practiceId: string, live: boolean): Promise<IteroStatus> {
    const response = await apiV2.get<ApiEnvelope<IteroStatus>>(`${iteroPath(practiceId)}/status`, {
        params: live ? { live: true } : undefined,
        timeout: ITERO_SLOW_CALL_TIMEOUT_MS
    });
    return response.data.data;
}

export async function fetchIteroClinics(practiceId: string): Promise<IteroClinic[]> {
    const response = await apiV2.get<ApiEnvelope<{ clinics: IteroClinic[] }>>(
        `${iteroPath(practiceId)}/clinics`,
        { timeout: ITERO_SLOW_CALL_TIMEOUT_MS }
    );
    return response.data.data.clinics;
}

export async function connectItero(practiceId: string, clinicId: number): Promise<IteroStatus> {
    const response = await apiV2.post<ApiEnvelope<IteroStatus>>(
        `${iteroPath(practiceId)}/connect`,
        { clinicId },
        { timeout: ITERO_SLOW_CALL_TIMEOUT_MS }
    );
    return response.data.data;
}

export async function refreshItero(practiceId: string): Promise<IteroRefreshResult> {
    const response = await apiV2.post<ApiEnvelope<IteroRefreshResult>>(
        `${iteroPath(practiceId)}/refresh`,
        undefined,
        { timeout: ITERO_SLOW_CALL_TIMEOUT_MS }
    );
    return response.data.data;
}

export async function disconnectItero(practiceId: string): Promise<IteroDisconnectResult> {
    const response = await apiV2.delete<ApiEnvelope<IteroDisconnectResult>>(iteroPath(practiceId));
    return response.data.data;
}

const ITERO_ERROR_FALLBACKS: Record<number, string> = {
    404: 'This practice could not be found.',
    409: "This iTero clinic isn't available to ODO's iTero account.",
    502: 'iTero rejected the request. Try again in a moment.',
    503: "Couldn't log in to iTero.",
    504: 'iTero took too long to respond. Try again in a moment.'
};

// The backend's own error.message wins; the fallback only covers responses without one.
export function iteroErrorMessage(err: unknown, fallback: string): string {
    const status = apiErrorStatus(err);
    const statusFallback = status === null ? undefined : ITERO_ERROR_FALLBACKS[status];
    return apiErrorMessage(err, statusFallback ?? fallback);
}
